import "./BillionairesCard.css";
import { renderBillionaireLayout } from "./BillionaireLayout";
import { renderNetWorth } from "./NetWorth";
import { type BillionairesResponse, type BillionaireItem, escapeHtml, formatChange, formatWorth, updatedLabel } from "./BillionaireTypes";

const API = "https://api.ayesearch.com/api/billionaires/search/";

function imageMarkup(person: BillionaireItem): string {
  return person.image_url
    ? `<img class="aye-billionaire-image" src="${escapeHtml(person.image_url)}" alt="${escapeHtml(person.name)}" loading="lazy" referrerpolicy="no-referrer" />`
    : `<div class="aye-billionaire-image-placeholder">${escapeHtml(person.name.charAt(0))}</div>`;
}

function rankingRow(person: BillionaireItem): string {
  const positive = Number(person.daily_change_usd || 0) >= 0;
  return `<article class="aye-billionaire-ranking-row"><div class="aye-billionaire-ranking-number">${person.rank}</div><div class="aye-billionaire-ranking-image">${imageMarkup(person)}</div><div class="aye-billionaire-ranking-person"><strong>${escapeHtml(person.name)}</strong><span>${escapeHtml(person.source_of_wealth || person.country || "")}</span></div><div class="aye-billionaire-ranking-worth"><strong>${escapeHtml(formatWorth(person.net_worth_usd))}</strong><span class="${positive ? "positive" : "negative"}">${escapeHtml(formatChange(person))}</span></div></article>`;
}

function renderRanking(container: HTMLElement, items: BillionaireItem[], sourceUrl: string): void {
  container.innerHTML = `<section class="aye-billionaires-ranking-card"><div class="aye-billionaires-ranking-header"><div><div class="aye-billionaire-kicker">AYESEARCH WEALTH TRACKER</div><h1>World's richest people</h1><p>Top ${items.length} cached from Forbes Real-Time Billionaires.</p></div><div class="aye-billionaires-ranking-updated">${escapeHtml(updatedLabel(items[0]?.last_seen_at || ""))}</div></div><div class="aye-billionaires-ranking-list">${items.map(rankingRow).join("")}</div><div class="aye-billionaire-source-row"><span>Tracked and cached by AyeSearch</span><span>·</span><a href="${escapeHtml(sourceUrl)}" target="_blank" rel="noopener noreferrer">Source: Forbes</a></div></section>`;
}

export async function resolveBillionaireResult(query: string): Promise<BillionairesResponse | null> {
  const url = new URL(API);
  url.searchParams.set("q", query);
  const response = await fetch(url.toString(), { method: "GET", credentials: "include", headers: { Accept: "application/json" } });
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) return null;
  const data = (await response.json()) as BillionairesResponse;
  if (response.status === 404) return null;
  if (!response.ok || data.error) throw new Error(data.error || `Billionaires API returned HTTP ${response.status}`);
  return data;
}

export function renderResolvedBillionaire(container: HTMLElement, data: BillionairesResponse, query: string): boolean {
  const sourceUrl = data.source?.url || "https://www.forbes.com/real-time-billionaires/";
  if (data.kind === "person" && data.person) {
    if (data.view === "net_worth" || /\bnet\s+worth\b/i.test(query)) renderNetWorth(container, data.person, sourceUrl, data.forbes_result);
    else renderBillionaireLayout(container, data.person, sourceUrl, data.forbes_result);
    return true;
  }
  if (data.kind === "ranking" && Array.isArray(data.items)) { renderRanking(container, data.items, sourceUrl); return true; }
  return false;
}

export async function renderBillionairesCard(container: HTMLElement, query: string): Promise<boolean> {
  container.innerHTML = `<section class="aye-billionaire-loading"><div class="aye-billionaire-loading-line"></div><div class="aye-billionaire-loading-value"></div><div class="aye-billionaire-loading-line short"></div></section>`;
  try {
    const data = await resolveBillionaireResult(query);
    if (!data) { container.innerHTML = ""; return false; }
    return renderResolvedBillionaire(container, data, query);
  } catch (error) {
    console.error("AyeSearch billionaire card failed:", error);
    container.innerHTML = `<section class="aye-billionaire-empty">Billionaire tracking is temporarily unavailable.</section>`;
    return true;
  }
}
